import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { Icon } from "@iconify/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, GridReadyEvent, SideBarDef } from "ag-grid-community";
import { toast } from "react-toastify";
import { downloadBlobObject } from "../../utils/DownloadFile";
import convertToBase64 from "../../utils/convertToBase64";
import { previewDocumentTemplate } from "../../api/Esop";
import AggridTooltip from "../../components/shared/AggridTooltip";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  SettingHeader,
  SettingsRow,
  VStack,
} from "../../components/utils";
import SearchInput from "../../components/shared/SearchInput";
import {
  GrantLetterTemplate,
  GrantLetterSetting,
} from "../../types/SiteSettings";
import {
  useGetGrantLetterSettings,
  useGetGrantLetterTemplates,
  useSetGrantLetterSettings,
} from "../../queries/siteSettings";
import { useUserDetailsV2 } from "../../queries";
import BulkUploadTemplatesDialog from "../../modals/BulkUploadTemplatesDialog";
import { Instructions } from "../../components/shared/Instructions";
import AlertDialog from "../../components/shared/AlertDialog";
import TemplateVariablesModal from "../transactions/Exercise/TemplateVariablesModal";

function getDefaultTo(t: GrantLetterTemplate): string {
  if (t.isPlatFormDefault) return "Company Wide";
  if (t.esopPlanId) return `Plan (${t.planName})`;
  if (t.grantLevelTemplate) return "Grant";
  return t.planName || "—";
}

const ActionIndicator = (props: {
  status: "ADDED" | "EDITED" | "DELETED" | undefined;
}): React.ReactElement => {
  const status = props.status;
  const colourMapper = {
    ADDED: "text-green-400",
    EDITED: "text-blue-400",
    DELETED: "text-red-400",
  };
  return (
    <span className={`pl-0.5 text-xxs ${colourMapper[status || "EDITED"]}`}>
      {status && ` will be ${status.toLowerCase()}`}
    </span>
  );
};

interface FormValues {
  overrideGrantLetter: boolean;
  grantLetterTemplates: GrantLetterTemplate[];
}

type TemplateTab = "plan" | "grant";

function GrantLetterTemplateSettings({
  onDirtyChange,
}: {
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const gridApi = useRef<GridApi | any>(null);
  const grantGridApi = useRef<GridApi | any>(null);
  const initializedTemplatesRef = useRef(false);
  const initializedSettingsRef = useRef(false);

  const [activeTab, setActiveTab] = useState<TemplateTab>("plan");
  const [uploadTemplateOpen, setUploadTemplateOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [pendingDeleteTemplate, setPendingDeleteTemplate] =
    useState<GrantLetterTemplate | null>(null);

  const { data: rawTemplates, refetch } = useGetGrantLetterTemplates();
  const { data: grantSettings } = useGetGrantLetterSettings();
  const { mutate: saveSettings, isLoading: savingSettings } =
    useSetGrantLetterSettings();

  const { data: userDetails } = useUserDetailsV2();
  const isInternalUser =
    userDetails?.emailId?.endsWith("@rulezero.com") ?? false;

  const templates: GrantLetterTemplate[] = useMemo(() => {
    if (Array.isArray(rawTemplates))
      return rawTemplates as GrantLetterTemplate[];
    if (rawTemplates && typeof rawTemplates === "object") {
      const obj = rawTemplates as Record<string, unknown>;
      if (Array.isArray(obj.grantLetterTemplates))
        return obj.grantLetterTemplates as GrantLetterTemplate[];
      if (Array.isArray(obj.data)) return obj.data as GrantLetterTemplate[];
    }
    return [];
  }, [rawTemplates]);

  const formik = useFormik<FormValues>({
    initialValues: {
      overrideGrantLetter: false,
      grantLetterTemplates: [],
    },
    enableReinitialize: false,
    onSubmit: (values) => {
      const changedTemplates = values.grantLetterTemplates.filter(
        (t) => t.isChanged || t.isDeleted || t.isAdded
      );
      const settingsChanged =
        grantSettings?.overrideGrantLetter !== values.overrideGrantLetter;
      if (!settingsChanged && changedTemplates.length === 0) {
        toast("No changes made", { type: "info", autoClose: 2000 });
        return;
      }
      const payload: Partial<GrantLetterSetting> = {
        overrideGrantLetter: values.overrideGrantLetter,
      };
      if (changedTemplates.length > 0) {
        payload.grantLetterTemplates = changedTemplates;
      }
      saveSettings(payload as GrantLetterSetting, {
        onSuccess: () => {
          toast.success("Settings saved successfully");
          setTimeout(() => {
            initializedTemplatesRef.current = false;
            refetch();
          }, 1000);
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.errorRaw || "Failed to save settings"
          );
        },
      });
    },
  });

  // Sync templates from server into formik once on initial load
  useEffect(() => {
    if (!initializedTemplatesRef.current && templates.length > 0) {
      formik.setFieldValue(
        "grantLetterTemplates",
        templates.map((t) => ({
          ...t,
          isChanged: false,
          isDeleted: false,
          isAdded: false,
        }))
      );
      initializedTemplatesRef.current = true;
    }
  }, [templates]);

  // Sync override toggle from server into formik once on initial load
  useEffect(() => {
    if (!initializedSettingsRef.current && grantSettings !== undefined) {
      formik.setFieldValue(
        "overrideGrantLetter",
        grantSettings.overrideGrantLetter ?? false
      );
      initializedSettingsRef.current = true;
    }
  }, [grantSettings]);

  // Notify parent of dirty state
  useEffect(() => {
    if (!onDirtyChange) return;
    const dirty =
      grantSettings?.overrideGrantLetter !==
        formik.values.overrideGrantLetter ||
      formik.values.grantLetterTemplates.some(
        (t) => t.isChanged || t.isDeleted || t.isAdded
      );
    onDirtyChange(dirty);
  }, [
    formik.values.overrideGrantLetter,
    formik.values.grantLetterTemplates,
    grantSettings,
  ]);

  async function downloadTemplate(
    s3Path: string,
    fileName = "GrantLetterTemplate.docx"
  ) {
    const res = await fetch(s3Path);
    const blob = await res.blob();
    downloadBlobObject(blob, fileName);
  }

  async function handlePreviewTemplate(data: GrantLetterTemplate) {
    const base64 =
      (data as any).planLevelGrantTemplate || (data as any).grantLevelTemplate;
    const fileName = `${
      data.templateName || data.planName || "GrantLetter"
    }.docx`;
    let fileObj: File;

    if (base64) {
      let base64Content = base64 as string;
      if (base64Content.includes(","))
        base64Content = base64Content.split(",")[1];
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      fileObj = new File([blob], fileName, {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
    } else if (data.s3Path) {
      const res = await fetch(data.s3Path);
      const blob = await res.blob();
      fileObj = new File([blob], fileName, {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
    } else {
      toast.error("No template file available for preview");
      return;
    }

    try {
      const responseBlob = await previewDocumentTemplate(
        fileObj,
        "GRANT_LETTER"
      );
      const url = URL.createObjectURL(responseBlob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `preview_${fileName}`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      let errorMessage = "Failed to generate preview";
      const errData = err?.response?.data;
      if (errData instanceof Blob) {
        try {
          const text = await errData.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed?.errorRaw || parsed?.message || errorMessage;
        } catch {
          // ignore JSON parse error
        }
      } else if (errData?.errorRaw) {
        errorMessage = errData.errorRaw;
      }
      toast.error(errorMessage);
    }
  }

  function handleBulkUploadClick() {
    setUploadTemplateOpen(true);
  }

  function openDeleteConfirmation(template: GrantLetterTemplate) {
    setPendingDeleteTemplate(template);
    setDeleteConfirmationOpen(true);
  }

  function closeDeleteConfirmation() {
    setDeleteConfirmationOpen(false);
    setPendingDeleteTemplate(null);
  }

  function confirmDeleteTemplate() {
    if (!pendingDeleteTemplate) return;

    const template = pendingDeleteTemplate as GrantLetterTemplate & {
      isDeleted?: boolean;
      isChanged?: boolean;
      s3Path?: string;
    };

    template.s3Path = "";
    template.isDeleted = true;
    template.isChanged = true;

    formik.setFieldValue("grantLetterTemplates", [
      ...formik.values.grantLetterTemplates,
    ]);
    setTimeout(() => {
      gridApi.current?.refreshCells();
      grantGridApi.current?.refreshCells();
    }, 0);

    toast('Template deleted. Click "Save Settings" to apply the changes.', {
      type: "info",
      autoClose: 3000,
    });
    closeDeleteConfirmation();
  }

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      wrapText: true,
      autoHeaderHeight: true,
      minWidth: 100,
      flex: 1,
      resizable: true,
      filter: false,
      cellClass: "multiline text-xs font-medium text-gray-dark leading-5",
      menuTabs: [],
      wrapHeaderText: true,
    }),
    []
  );

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.current = params.api;
  };

  const onGrantGridReady = (params: GridReadyEvent) => {
    grantGridApi.current = params.api;
  };

  const getTemplateTypeOrder = (template: GrantLetterTemplate): number => {
    if (template.isPlatFormDefault) return 0;
    if (template.esopPlanId || template.planName === "ALL PLANS") return 1;
    return 2;
  };

  const [grantSearchValue, setGrantSearchValue] = useState("");

  // Tab 1: platform default + plan-level templates
  const planTemplates = useMemo(
    () =>
      formik.values.grantLetterTemplates
        .filter(
          (t) =>
            !t.isDeleted &&
            !t.grantLevelTemplate &&
            (t.isPlatFormDefault ||
              !!t.esopPlanId ||
              t.planName === "ALL PLANS" ||
              t.entityType === "PLAN")
        )
        .sort((a, b) => {
          const orderA = getTemplateTypeOrder(a);
          const orderB = getTemplateTypeOrder(b);
          if (orderA !== orderB) return orderA - orderB;
          return (a.templateName || a.planName || "").localeCompare(
            b.templateName || b.planName || ""
          );
        }),
    [formik.values.grantLetterTemplates]
  );

  // Tab 2: grant-specific templates
  const grantTemplates = useMemo(
    () =>
      formik.values.grantLetterTemplates
        .filter(
          (t) =>
            !t.isDeleted &&
            !t.isPlatFormDefault &&
            !t.esopPlanId &&
            t.planName !== "ALL PLANS" &&
            t.entityType !== "PLAN"
        )
        .sort((a, b) =>
          (a.templateName || a.planName || "").localeCompare(
            b.templateName || b.planName || ""
          )
        ),
    [formik.values.grantLetterTemplates]
  );

  const filteredGrantTemplates = useMemo(() => {
    if (!grantSearchValue.trim()) return grantTemplates;
    const lower = grantSearchValue.toLowerCase();
    return grantTemplates.filter(
      (t) =>
        (t.templateName || "").toLowerCase().includes(lower) ||
        (t.planName || "").toLowerCase().includes(lower)
    );
  }, [grantTemplates, grantSearchValue]);

  // AG Grid column definitions
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "NAME",
        field: "templateName",
        editable: true,
        width: 300,
        wrapText: true,
        autoHeight: true,
        cellEditor: "agTextCellEditor",
        cellStyle: {
          lineHeight: "20px",
          paddingTop: "15px",
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        },
        valueGetter: (params: any) => {
          const d = params.data;
          const isPlan =
            !d.isPlatFormDefault &&
            (!!d.esopPlanId || d.planName === "ALL PLANS");
          return isPlan ? d.planName || "" : d.templateName || d.planName || "";
        },
        valueSetter: (params: any) => {
          const { newValue, data } = params;
          if (data.templateName !== newValue) {
            data.templateName = newValue;
            if (!data.isAdded) data.isChanged = true;
            formik.setFieldValue("grantLetterTemplates", [
              ...formik.values.grantLetterTemplates,
            ]);
          }
          return true;
        },
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-1">
            {params.value}
            <ActionIndicator
              status={
                params.data.isAdded
                  ? "ADDED"
                  : params.data.isDeleted
                  ? "DELETED"
                  : params.data.isChanged
                  ? "EDITED"
                  : undefined
              }
            />
          </div>
        ),
      },
      {
        headerName: "DEFAULT TO",
        field: "defaultTo",
        valueGetter: (params: any) => getDefaultTo(params.data),
        width: 300,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: "20px",
          paddingTop: "15px",
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        },
      },
      {
        headerName: "ACTIONS",
        pinned: "right",
        filter: false,
        sortable: false,
        width: 220,
        cellRenderer: (params: any) => {
          const data = params.data;
          const isDefault = !!data.isPlatFormDefault;
          const isPlan =
            !isDefault &&
            (!!data.esopPlanId ||
              data.planName === "ALL PLANS" ||
              data.entityType === "PLAN");
          const isGrant = !isDefault && !isPlan;
          const viewDisabled = !data.s3Path;
          const deleteDisabled = isPlan && !data.s3Path;
          return (
            <Box className="mt-2 px-auto py-auto">
              <div className="flex items-center justify-start gap-2 flex-wrap">
                {/* VIEW */}
                <button
                  onClick={() => {
                    if (data.s3Path) {
                      downloadTemplate(
                        data.s3Path,
                        `${
                          data.templateName || data.planName || "GrantLetter"
                        }-GrantLetterTemplate.docx`
                      );
                    }
                  }}
                  title="View/Download"
                  disabled={viewDisabled}
                  className={`p-1 transition-transform ${
                    viewDisabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-orange-501 hover:scale-110"
                  }`}
                >
                  <Icon icon="ph:eye" width="16" height="16" />
                </button>

                {/* EDIT — plan and grant only */}
                {(isPlan || isGrant) && (
                  <label
                    className="p-1 cursor-pointer transition-transform text-orange-501 hover:scale-110"
                    title="Upload/Replace"
                  >
                    <input
                      type="file"
                      accept=".docx"
                      style={{ display: "none" }}
                      onChange={async (e) => {
                        if (!e?.target?.files?.[0]) return;
                        const file = e.target.files[0];
                        const fileExt = file.name
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        if (fileExt !== "docx") {
                          toast.error("Only .docx files are supported");
                          return;
                        }
                        const base64 = (await convertToBase64(file)) as string;
                        if (isPlan) {
                          data.planLevelGrantTemplate = base64;
                        } else {
                          data.grantLevelTemplate = base64;
                        }
                        data.isChanged = true;
                        if (!data.s3Path) data.isAdded = true;
                        formik.setFieldValue("grantLetterTemplates", [
                          ...formik.values.grantLetterTemplates,
                        ]);
                        setTimeout(() => gridApi.current?.refreshCells(), 0);
                      }}
                    />
                    <Icon icon="akar-icons:pencil" width="16" height="16" />
                  </label>
                )}

                {isInternalUser && (isPlan || isGrant) && (
                  <AggridTooltip
                    text="Download preview docx - renders your template with sample data so you can see how the final document will look"
                    _className="w-[300px]"
                  >
                    <div className="mt-2">
                      <button
                        onClick={() => handlePreviewTemplate(data)}
                        disabled={viewDisabled}
                        className={`p-1 transition-transform ${
                          viewDisabled
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-orange-501 hover:scale-110"
                        }`}
                      >
                        <Icon icon="mdi:file-download" width="16" height="16" />
                      </button>
                    </div>
                  </AggridTooltip>
                )}

                {/* DELETE — plan and grant only */}
                {(isPlan || isGrant) && (
                  <button
                    disabled={deleteDisabled}
                    onClick={() => {
                      openDeleteConfirmation(data);
                    }}
                    title="Delete"
                    className={`p-1 transition-transform ${
                      deleteDisabled
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-orange-501 hover:scale-110"
                    }`}
                  >
                    <Icon
                      icon="material-symbols:delete"
                      width="16"
                      height="16"
                    />
                  </button>
                )}
              </div>
            </Box>
          );
        },
      },
    ],
    [formik.values.grantLetterTemplates, isInternalUser]
  );

  // Column defs for grant-specific templates tab
  const grantColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "FILE",
        field: "templateName",
        flex: 1,
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: "20px",
          paddingTop: "15px",
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        },
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2">
            <div>
              <p className="text-xs font-medium text-gray-dark leading-5">
                {params.data.templateName || params.data.planName || "—"}
              </p>
              {params.data.uploadedBy && (
                <p className="text-xs text-gray-400">
                  Uploaded by {params.data.uploadedBy}
                </p>
              )}
              <ActionIndicator
                status={
                  params.data.isAdded
                    ? "ADDED"
                    : params.data.isDeleted
                    ? "DELETED"
                    : params.data.isChanged
                    ? "EDITED"
                    : undefined
                }
              />
            </div>
          </div>
        ),
      },
      {
        headerName: "UPLOADED",
        field: "uploadedAt",
        width: 220,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          lineHeight: "20px",
          paddingTop: "15px",
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        },
        valueGetter: (params: any) => {
          const raw = params.data.uploadedAt;
          if (!raw) return "—";
          const d = new Date(raw);
          return Number.isNaN(d.getTime())
            ? raw
            : d.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });
        },
      },
      {
        headerName: "ACTIONS",
        pinned: "right",
        filter: false,
        sortable: false,
        width: 160,
        cellRenderer: (params: any) => {
          const data = params.data;
          const viewDisabled = !data.s3Path;
          return (
            <Box className="mt-2 px-auto py-auto">
              <div className="flex items-center justify-start gap-2">
                <button
                  onClick={() => {
                    if (data.s3Path) {
                      downloadTemplate(
                        data.s3Path,
                        `${
                          data.templateName || "GrantLetter"
                        }-GrantLetterTemplate.docx`
                      );
                    }
                  }}
                  title="View/Download"
                  disabled={viewDisabled}
                  className={`p-1 transition-transform ${
                    viewDisabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-orange-501 hover:scale-110"
                  }`}
                >
                  <Icon icon="ph:eye" width="16" height="16" />
                </button>
                {isInternalUser && (
                  <AggridTooltip
                    text="Download preview docx - renders your template with sample data so you can see how the final document will look"
                    _className="w-[400px]"
                  >
                    <div className="mt-2">
                      <button
                        onClick={() => handlePreviewTemplate(data)}
                        disabled={viewDisabled}
                        className={`p-1 transition-transform ${
                          viewDisabled
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-orange-501 hover:scale-110"
                        }`}
                      >
                        <Icon icon="mdi:file-download" width="16" height="16" />
                      </button>
                    </div>
                  </AggridTooltip>
                )}
                <button
                  onClick={() => {
                    openDeleteConfirmation(data);
                  }}
                  title="Delete"
                  className="p-1 transition-transform text-orange-501 hover:scale-110"
                >
                  <Icon icon="material-symbols:delete" width="16" height="16" />
                </button>
              </div>
            </Box>
          );
        },
      },
    ],
    [formik.values.grantLetterTemplates, isInternalUser]
  );

  return (
    <>
      <Box className="w-full p-4 bg-white rounded-lg ">
        <VStack className="justify-between p-4 gap-9">
          <Box className="items-center mt-6 text-lg font-medium text-gray-dark">
            <SettingHeader
              title="Grant Letter Template Settings"
              description="Upload and manage grant letter templates for plans, grants, or all plans"
            />
          </Box>
          <Box className="w-full border-t-2 border-dotted" />

          {grantSettings?.disableAutoGrantLetterGeneration && (
            <VStack className="p-4 text-black-501 bg-yellow-100 rounded">
              <HStack className="pt-0 justify-start">
                <HStack className="gap-1 pl-1 items-start">
                  <Icon
                    icon="material-symbols:info-outline-rounded"
                    height={18}
                    width={18}
                    className="w-full text-xs font-medium text-black-501 rounded"
                  />
                  <p className="text-sm">Note</p>
                  <p> : </p>
                  <p className="text-xs pt-0.5 whitespace-nowrap">
                    Auto grant letter generation is currently turned off. Grant
                    letters will not be generated automatically upon grant
                    creation.
                  </p>
                </HStack>
              </HStack>
            </VStack>
          )}

          {!grantSettings?.disableAutoGrantLetterGeneration && (
            <>
              <VStack className="w-full gap-10">
                <SettingsRow
                  id="grant-overrideGrantLetter"
                  label="Override default Grant letter template"
                  description="If enabled, the default grant letter template will be overridden."
                  type="toggle"
                  name="overrideGrantLetter"
                  value={formik.values.overrideGrantLetter}
                  onToggle={() =>
                    formik.setFieldValue(
                      "overrideGrantLetter",
                      !formik.values.overrideGrantLetter
                    )
                  }
                  disabled={savingSettings}
                />
              </VStack>

              {formik.values.overrideGrantLetter && (
                <>
                  {/* Tab header row */}
                  <HStack className="justify-between w-full items-center">
                    <VStack className="gap-1">
                      <p className="text-lg font-semibold text-gray-dark">
                        Grant Letter Templates
                      </p>
                      <p className="text-xs font-medium text-[#B5B5C3]">
                        {activeTab === "plan"
                          ? planTemplates.length
                          : filteredGrantTemplates.length}{" "}
                        {activeTab === "plan" ? "Templates" : "Files"}
                      </p>
                    </VStack>
                    <HStack className="gap-4">
                      {isInternalUser && (
                        <ButtonPrimary1
                          className="px-4 py-2"
                          onClick={() => setShowVariablesModal(true)}
                        >
                          <HStack className="items-center gap-1">
                            <Icon
                              icon="material-symbols:menu-book-outline"
                              width={16}
                            />
                            <span>View Template Variables</span>
                          </HStack>
                        </ButtonPrimary1>
                      )}
                      <HStack className="gap-4">
                        {(["plan", "grant"] as TemplateTab[]).map((tab) => (
                          <Box
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`text-xs3 font-semibold cursor-pointer py-1 ${
                              activeTab === tab
                                ? "border-b-2 border-solid border-red-500 text-red-500"
                                : "text-gray-201"
                            }`}
                          >
                            {tab === "plan"
                              ? "Default Templates"
                              : "Custom Grant Letter Templates"}
                          </Box>
                        ))}
                      </HStack>
                    </HStack>
                  </HStack>

                  {/* Tab 1 — Plan templates */}
                  {activeTab === "plan" && (
                    <>
                      <HStack className="justify-between w-full">
                        <VStack className="items-center w-1/3 gap-4">
                          <SearchInput
                            placeholder="Search plan templates..."
                            onChange={(e: any) => {
                              gridApi.current?.setQuickFilter(e.target.value);
                            }}
                          />
                        </VStack>
                      </HStack>

                      <HStack className="justify-between w-full">
                        <Box
                          style={{
                            height: `${
                              (planTemplates.length >= 10
                                ? 10
                                : planTemplates.length + 3) * 60
                            }px`,
                          }}
                          className="w-full h-full overflow-x-auto bg-black ag-theme-material"
                        >
                          <AgGridReact
                            alwaysShowHorizontalScroll
                            rowClass="border-t border-dashed cursor-pointer hover:bg-slate-50"
                            onGridReady={onGridReady}
                            defaultColDef={defaultColDef}
                            rowData={planTemplates}
                            suppressCopyRowsToClipboard={true}
                            suppressCopySingleCellRanges={true}
                            suppressCellFocus={true}
                            suppressMenuHide={true}
                            sideBar={sideBar}
                            columnDefs={columnDefs}
                            rowMultiSelectWithClick={true}
                            suppressRowClickSelection={true}
                            stopEditingWhenCellsLoseFocus={true}
                            overlayNoRowsTemplate='<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Templates Found</span>'
                          />
                        </Box>
                      </HStack>
                    </>
                  )}

                  {/* Tab 2 — Grant letter files */}
                  {activeTab === "grant" && (
                    <>
                      <HStack className="justify-between w-full items-center">
                        <VStack className="items-center w-1/3 gap-4">
                          <SearchInput
                            placeholder="Search files..."
                            onChange={(e: any) => {
                              setGrantSearchValue(e.target.value);
                            }}
                          />
                        </VStack>
                        <ButtonPrimary
                          type="button"
                          onClick={handleBulkUploadClick}
                        >
                          <HStack className="items-center gap-1">
                            Bulk Upload
                          </HStack>
                        </ButtonPrimary>
                      </HStack>

                      <HStack className="justify-between w-full">
                        <Box
                          style={{
                            height: `${
                              (filteredGrantTemplates.length >= 10
                                ? 10
                                : filteredGrantTemplates.length + 3) * 60
                            }px`,
                          }}
                          className="w-full h-full overflow-x-auto bg-black ag-theme-material"
                        >
                          <AgGridReact
                            alwaysShowHorizontalScroll
                            rowClass="border-t border-dashed cursor-pointer hover:bg-slate-50"
                            onGridReady={onGrantGridReady}
                            defaultColDef={defaultColDef}
                            rowData={filteredGrantTemplates}
                            suppressCopyRowsToClipboard={true}
                            suppressCopySingleCellRanges={true}
                            suppressCellFocus={true}
                            suppressMenuHide={true}
                            sideBar={sideBar}
                            columnDefs={grantColumnDefs}
                            rowMultiSelectWithClick={true}
                            suppressRowClickSelection={true}
                            stopEditingWhenCellsLoseFocus={true}
                            overlayNoRowsTemplate='<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Files Found</span>'
                          />
                        </Box>
                      </HStack>
                    </>
                  )}
                </>
              )}

              <HStack className="justify-end">
                <ButtonPrimary
                  type="button"
                  onClick={() => formik.handleSubmit()}
                  loading={savingSettings}
                  disabled={
                    savingSettings ||
                    (grantSettings?.overrideGrantLetter ===
                      formik.values.overrideGrantLetter &&
                      !formik.values.grantLetterTemplates.some(
                        (t) => t.isChanged || t.isDeleted || t.isAdded
                      ))
                  }
                >
                  Save Settings
                </ButtonPrimary>
              </HStack>
            </>
          )}
        </VStack>
      </Box>

      <BulkUploadTemplatesDialog
        open={uploadTemplateOpen}
        onClose={() => setUploadTemplateOpen(false)}
        title="Bulk Upload Templates"
        existingFileNames={grantTemplates
          .map((t) => t.templateName || t.planName || "")
          .filter(Boolean)}
        onSuccess={() => {
          initializedTemplatesRef.current = false;
          refetch();
        }}
        bodyExtras={
          <Instructions
            data={[
              "Upload .docx or .zip files only",
              "Valid files will be saved as templates",
              "Invalid files will be skipped",
              "An upload summary will be shown after each upload",
            ]}
          />
        }
      />
      <TemplateVariablesModal
        open={showVariablesModal}
        onClose={() => setShowVariablesModal(false)}
        templateType="GRANT_LETTER"
      />
      <AlertDialog
        open={deleteConfirmationOpen}
        onClose={closeDeleteConfirmation}
        message="You are about to permanently delete this grant letter template. This template may be associated with one or more existing grants. Upon regeneration, those grants will revert to their applicable default templates. This action cannot be undone. Do you wish to proceed?"
        primaryActionText="Delete Template"
        secondaryActionText="Cancel"
        onPrimaryAction={confirmDeleteTemplate}
        onSecondaryAction={closeDeleteConfirmation}
      />
    </>
  );
}

export default GrantLetterTemplateSettings;
